import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { getToken, saveAuth } from '../utils/userData'
import { trackEvent } from '../utils/mixpanel'

const MidDayReset = () => {
  const navigate = useNavigate()
  
  // Page states: 'loading', 'marketing'
  const [pageState, setPageState] = useState('loading')
  
  // Auth states - tracked separately
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isEnrolled, setIsEnrolled] = useState(false)
  
  // Auto-enroll loading state (for logged-in users clicking signup)
  const [isAutoEnrolling, setIsAutoEnrolling] = useState(false)
  
  // Modal states
  const [showModal, setShowModal] = useState(false)
  // Modal view: 'form', 'otp', 'success'
  const [modalView, setModalView] = useState('form')
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  })
  
  // OTP state
  const [otp, setOtp] = useState(['', '', '', ''])
  const [otpError, setOtpError] = useState('')
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const otpRefs = useRef([])
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const benefits = [
    'A pause in the middle of your day',
    '30 mins of breathwork and meditation',
    'Explore new techniques',
    'Build Consistency',
    'Practice with community',
  ]

  // Check auth and enrollment on mount
  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Mid Day Reset' })
    checkEnrollmentStatus()
  }, [])

  // Focus first OTP input when modal view changes to OTP
  useEffect(() => {
    if (modalView === 'otp' && otpRefs.current[0]) {
      otpRefs.current[0].focus()
    }
  }, [modalView])

  const checkEnrollmentStatus = async () => {
    const token = getToken()
    
    if (!token) {
      // No token - not logged in
      setIsLoggedIn(false)
      setIsEnrolled(false)
      setPageState('marketing')
      return
    }

    // Has token - user is logged in
    setIsLoggedIn(true)

    try {
      // Check if user is enrolled in mid-day-reset
      const response = await api.get('/api/programs/mid-day-reset/enrollment')
      
      if (response.data.success) {
        // User is enrolled
        setIsEnrolled(true)
      } else {
        // User is logged in but not enrolled
        setIsEnrolled(false)
      }
      setPageState('marketing')
    } catch (error) {
      // Error checking enrollment - user is logged in but not enrolled (or error)
      console.error('Enrollment check error:', error)
      setIsEnrolled(false)
      setPageState('marketing')
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle CTA button click
  const handleCtaClick = async () => {
    if (isEnrolled) {
      // Already enrolled - go to dashboard
      goToDashboard()
      return
    }

    if (isLoggedIn) {
      // Logged in but not enrolled - auto-enroll directly
      await handleAutoEnroll()
    } else {
      // Not logged in - show form modal
      setShowModal(true)
    }
  }

  // Auto-enroll a logged-in user
  const handleAutoEnroll = async () => {
    const token = getToken()
    if (!token) {
      // Token expired or missing, show modal instead
      setIsLoggedIn(false)
      setShowModal(true)
      return
    }

    setIsAutoEnrolling(true)

    try {
      const response = await api.post('/api/programs/mid-day-reset/auto-enroll', {})

      if (response.data.success) {
        // Update enrollment state
        setIsEnrolled(true)
      }
    } catch (err) {
      console.error('Auto-enroll error:', err)
      setError(err.response?.data?.message || 'Failed to enroll. Please try again.')
    } finally {
      setIsAutoEnrolling(false)
    }
  }

  // Form submission - send OTP for email verification
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      // Request OTP - create user if not exists
      await api.post('/api/auth/otp', {
        email: formData.email,
        programSlug: 'mid-day-reset',
        createIfNotExists: true,
      })
      
      // Switch to OTP view
      setModalView('otp')
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Failed to send verification code. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // OTP handling
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setOtpError('')

    // Auto-advance to next input
    if (value && index < 3) {
      otpRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all digits are entered
    if (value && index === 3 && newOtp.every(d => d !== '')) {
      handleVerifyOtp(newOtp.join(''))
    }
  }

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 4)
    if (/^\d{1,4}$/.test(pastedData)) {
      const digits = pastedData.split('')
      const newOtp = [...otp]
      digits.forEach((digit, i) => {
        if (i < 4) newOtp[i] = digit
      })
      setOtp(newOtp)
      setOtpError('')
      
      // Focus on the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex(d => d === '')
      if (nextEmptyIndex !== -1) {
        otpRefs.current[nextEmptyIndex]?.focus()
      } else {
        otpRefs.current[3]?.focus()
        // Auto-submit if all filled
        handleVerifyOtp(newOtp.join(''))
      }
    }
  }

  const handleVerifyOtp = async (code = otp.join('')) => {
    if (code.length !== 4) {
      setOtpError('Please enter the complete 4-digit code')
      return
    }

    setOtpError('')
    setIsVerifyingOtp(true)

    try {
      // Verify OTP
      const verifyResponse = await api.post('/api/auth/verify-otp', {
        email: formData.email,
        code: code,
        programSlug: 'mid-day-reset',
      })

      if (verifyResponse.data.success) {
        const { needsRegistration, enrollment, token } = verifyResponse.data.data

        if (needsRegistration) {
          // New user or user without name - complete registration with enrollment
          const completeResponse = await api.post('/api/auth/complete-registration', {
            email: formData.email,
            name: formData.name,
            phone: formData.phone,
            programSlug: 'mid-day-reset',
          })

          if (completeResponse.data.success) {
            const { token: newToken } = completeResponse.data.data
            
            // Save auth token
            saveAuth(newToken, null)
            
            // Update states
            setIsLoggedIn(true)
            setIsEnrolled(true)
            
            // Show success
            setModalView('success')
          }
        } else {
          // Existing user with profile - save token first, then auto-enroll
          saveAuth(token, null)
          setIsLoggedIn(true)
          
          // Check if already enrolled from verify response
          if (enrollment) {
            setIsEnrolled(true)
            setModalView('success')
          } else {
            // Need to enroll
            const enrollResponse = await api.post('/api/programs/mid-day-reset/auto-enroll', {})

            if (enrollResponse.data.success) {
              setIsEnrolled(true)
            }
            
            setModalView('success')
          }
        }
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Invalid verification code')
      setOtp(['', '', '', ''])
      otpRefs.current[0]?.focus()
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleResendOtp = async () => {
    try {
      await api.post('/api/auth/otp', {
        email: formData.email,
        programSlug: 'mid-day-reset',
        createIfNotExists: true,
      })
      setOtpError('')
      setOtp(['', '', '', ''])
      otpRefs.current[0]?.focus()
    } catch (err) {
      setOtpError('Failed to resend code. Please try again.')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    // Reset modal state after animation
    setTimeout(() => {
      setModalView('form')
      setFormData({ name: '', email: '', phone: '' })
      setOtp(['', '', '', ''])
      setError('')
      setOtpError('')
    }, 300)
  }

  const goToDashboard = () => {
    navigate('/mid-day-reset/dashboard')
  }

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </motion.div>
      </div>
    )
  }

  // Determine CTA button text
  const getCtaButtonText = () => {
    if (isAutoEnrolling) return 'Enrolling...'
    if (isEnrolled) return 'Go to Dashboard'
    return 'Sign Up Now'
  }

  // Marketing page
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-20 w-60 h-60 bg-teal-200/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Logo/Branding */}
            <div className="mb-8">
              <span className="inline-block text-2xl md:text-3xl font-black tracking-tight">
                <span className="text-gray-900">build</span>
                <span className="text-primary-600">3</span>
              </span>
              <span className="ml-2 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                MID-DAY RESET
              </span>
            </div>

            {/* Subtitle */}
            <h2 className="text-2xl md:text-3xl font-semibold text-primary-600 mb-8">
              Breathwork & Meditation
            </h2>

            {/* Tagline badges */}
            <div className="flex justify-center items-center gap-4 mb-12">
              {['Guided', 'Online', 'Free'].map((tag, index) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-800 font-semibold shadow-sm border border-gray-100"
                >
                  {tag}
                </motion.span>
              ))}
            </div>

            {/* Main content card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-xl p-8 md:p-12 border border-white/50">
                {/* Schedule box */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 mb-6 border border-gray-200"
                >
                  <p className="text-xl font-bold text-gray-900 mb-1">Ongoing</p>
                  <p className="text-lg font-semibold text-gray-700">Mon-Fri</p>
                  <p className="text-2xl font-bold text-primary-600">1:00 - 1:30pm</p>
                </motion.div>

                {/* Enrolled Message */}
                {isEnrolled && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 }}
                    className="text-green-600 font-medium text-center mb-3"
                  >
                    You&apos;re already signed up for Mid-Day Reset.
                  </motion.p>
                )}

                {/* Error message for auto-enroll failures */}
                {error && !showModal && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-red-600 font-medium text-center mb-3"
                  >
                    {error}
                  </motion.p>
                )}

                {/* CTA Button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCtaClick}
                  disabled={isAutoEnrolling}
                  className="w-full py-4 px-8 bg-gradient-to-r from-primary-600 to-teal-600 text-white text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 mb-8 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isAutoEnrolling && (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {getCtaButtonText()}
                </motion.button>

                {/* Benefits list */}
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-primary-500 to-teal-500 flex-shrink-0" />
                      <span className="text-gray-700 text-lg">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Footer branding */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="mt-12 flex items-center justify-center gap-3"
            >
              <img
                src="/logo-min.png"
                alt="Wellness Loop"
                className="w-12 h-12 object-contain"
              />
              <span className="text-primary-700 font-semibold text-lg">
                Wellness Loop
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Signup Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary-600 to-teal-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Mid-Day Reset
                    </h2>
                    <p className="text-white/90 text-sm">
                      Breathwork & Meditation
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Success View */}
                {modalView === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      You&apos;re In!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Welcome to Mid-Day Reset! You can now access your dashboard.
                    </p>
                    <button
                      onClick={goToDashboard}
                      className="px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      Go to Dashboard
                    </button>
                  </motion.div>
                )}

                {/* OTP Verification View */}
                {modalView === 'otp' && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="py-4"
                  >
                    {/* Info message */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <p className="text-sm text-blue-800 font-medium">
                            Verify your email
                          </p>
                          <p className="text-sm text-blue-600 mt-1">
                            We&apos;ve sent a verification code to <span className="font-semibold">{formData.email}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <p className="text-center text-gray-600 mb-6">
                      Enter the 4-digit code to continue
                    </p>

                    {/* OTP Input */}
                    <div className="flex justify-center gap-3 mb-4">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => (otpRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={index === 0 ? handleOtpPaste : undefined}
                          className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                          disabled={isVerifyingOtp}
                        />
                      ))}
                    </div>

                    {otpError && (
                      <p className="text-red-600 text-sm text-center mb-4">{otpError}</p>
                    )}

                    <button
                      onClick={() => handleVerifyOtp()}
                      disabled={isVerifyingOtp || otp.some(d => d === '')}
                      className="w-full py-4 bg-gradient-to-r from-primary-600 to-teal-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isVerifyingOtp ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </>
                      ) : (
                        'Verify & Join'
                      )}
                    </button>

                    <button
                      onClick={handleResendOtp}
                      className="w-full mt-3 py-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                    >
                      Resend Code
                    </button>

                    <button
                      onClick={() => {
                        setModalView('form')
                        setOtp(['', '', '', ''])
                        setOtpError('')
                      }}
                      className="w-full mt-2 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors"
                    >
                      ← Back to form
                    </button>
                  </motion.div>
                )}

                {/* Form View */}
                {modalView === 'form' && (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 bg-gradient-to-r from-primary-600 to-teal-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending code...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-500 mt-4">
                      By signing up, you agree to receive updates about the sessions.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MidDayReset
